import {
  Grid,
  CardProps,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Paper,
  Stack,
  Collapse,
  Box,
  PaperProps,
  Button,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { TP_COLORS } from '../../../constants';
import GetBreakPointName from '../../../theme/getBreakpoint';
import {
  getUrlConvertedString,
  getSubjectColorNew,
  getSubjectIconName,
  isMobileScreen,
  checkSubjectCodeHide,
} from '../../../helper';
import { ICurriculumForHome } from '../../../store/CurriculumSlice';

const paperStyles: PaperProps['sx'] = {
  padding: '20px',
  marginTop: '20px',
  borderRadius: '20px',
  border: '1px solid #CBD5E1',
};

interface CurriculumsProps {
  curriculumsForHomePage: Array<ICurriculumForHome>;
  selectedCurriculum: ICurriculumForHome | undefined;
}

const getRowItemCount = (breakPoint: string): number => {
  switch (breakPoint) {
    case 'xs':
      return 1;
    case 'sm':
      return 2;
    case 'md':
      return 3;
    case 'lg':
      return 4;
    default:
      return 4;
  }
};

const Curriculums: React.FC<CurriculumsProps> = ({
  curriculumsForHomePage,
  selectedCurriculum,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const isMobile = isMobileScreen();

  const [selectedCurriculumIndex, setSelectedCurriculumIndex] = useState<
    number | undefined
  >(undefined);
  const [isHover, setIsHover] = useState<number | undefined>(undefined);
  const [rowItemCount, setRowItemCount] = useState<number>(4);
  const [curriculumsList, setCurriculumsList] = useState<
    Array<ICurriculumForHome>
  >([]);

  const breakPoint = GetBreakPointName();

  const isSubjectCodeHide = checkSubjectCodeHide(
    selectedCurriculum?.name || ''
  );

  useEffect(() => {
    if (breakPoint) {
      setRowItemCount(getRowItemCount(breakPoint));
    }
  }, [breakPoint]);

  useEffect(() => {
    if (curriculumsForHomePage.length) {
      let curriculumIndex = 0;
      if (params.curriculum) {
        curriculumIndex = findCurriculumIndex(params.curriculum);
        setSelectedCurriculumIndex(
          (Math.floor(curriculumIndex / rowItemCount) + 1) * rowItemCount
        );
      }

      const mobileLimit = 4;
      if (isMobile && mobileLimit > curriculumIndex) {
        setCurriculumsList(curriculumsForHomePage.slice(0, mobileLimit));
      } else {
        setCurriculumsList(curriculumsForHomePage);
      }
    }
  }, [curriculumsForHomePage]);

  const findCurriculumIndex = (curriculumName: string) =>
    curriculumsForHomePage.findIndex(
      (curriculumForHomePage) =>
        getUrlConvertedString(curriculumForHomePage.name) ===
        getUrlConvertedString(curriculumName.trim())
    );

  const RENDER_CURRICULUMS = (start: number, end: number) => {
    if (curriculumsList.length) {
      return (
        <Grid container spacing={2} paddingTop="16px">
          {curriculumsList
            .slice(start, end)
            .map((curriculumForHomePage, index) => {
              const urlConvertedCurriculum = getUrlConvertedString(
                curriculumForHomePage.name
              );

              const isSelected =
                params.curriculum &&
                params.curriculum === urlConvertedCurriculum;

              const curriculumCardStyles: CardProps['sx'] = {
                border: `1px solid ${TP_COLORS.tutopiyaGreenMain.light}`,
                borderRadius: '10px',
                textAlign: 'center',
                color: 'primary.main',
              };

              if (isSelected) {
                curriculumCardStyles.backgroundColor = 'primary.main';
                curriculumCardStyles.color = TP_COLORS.white;
              }

              return (
                <Grid key={index} item xs={12} sm={6} md={4} lg={3}>
                  <Card elevation={0} sx={curriculumCardStyles}>
                    <CardActionArea
                      sx={{ minHeight: '70px', display: 'flex' }}
                      onClick={() => {
                        const curriculumIndex = findCurriculumIndex(
                          curriculumForHomePage.name
                        );
                        setSelectedCurriculumIndex(
                          (Math.floor(curriculumIndex / rowItemCount) + 1) *
                            rowItemCount
                        );
                        navigate(
                          isSelected
                            ? `${process.env.PUBLIC_URL}`
                            : `${process.env.PUBLIC_URL}/${urlConvertedCurriculum}`
                        );
                      }}
                    >
                      <CardContent style={{ padding: '3px 5px' }}>
                        <Typography
                          component="h3"
                          variant="subtitle2"
                          fontSize="15px"
                        >
                          {curriculumForHomePage.name}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
        </Grid>
      );
    }

    return null;
  };

  return (
    <Box margin="10px 0px">
      {RENDER_CURRICULUMS(0, selectedCurriculumIndex || 0)}

      {/* Subject List */}
      {selectedCurriculum &&
      selectedCurriculum.subjects &&
      selectedCurriculum.subjects.length ? (
        <Collapse in={Boolean(params.curriculum)}>
          <Paper sx={paperStyles}>
            <Grid container spacing={2}>
              {selectedCurriculum.subjects.map((subjectItem, index) => {
                const subjectIndex = index + 1;

                const curriculum = getUrlConvertedString(
                  selectedCurriculum ? selectedCurriculum.name : ''
                );
                const subject = getUrlConvertedString(subjectItem.name);
                const subjectLevel = getUrlConvertedString(
                  subjectItem.subjectLevel
                );
                const subjectCode = getUrlConvertedString(
                  subjectItem.subjectCode
                );

                const onHover = isHover && isHover === subjectIndex;

                return (
                  <Grid key={subjectIndex} item xs={12} sm={6} md={4} lg={3}>
                    <Card
                      elevation={0}
                      sx={{
                        backgroundColor: `${getSubjectColorNew(
                          subjectItem.name
                        )}1A`,
                        borderRadius: '7px',
                        '&:hover': {
                          backgroundColor: getSubjectColorNew(subjectItem.name),
                        },
                      }}
                    >
                      <CardActionArea
                        // onMouseEnter={() => setIsHover(subjectIndex)}
                        onMouseLeave={() => setIsHover(undefined)}
                        onMouseOver={() => setIsHover(subjectIndex)}
                        onClick={() =>
                          navigate(
                            `${process.env.PUBLIC_URL}/dashboard/${curriculum}/${subject}/${subjectLevel}/${subjectCode}`
                          )
                        }
                      >
                        <CardContent>
                          <Stack direction="row" justifyContent="space-between">
                            <Box>
                              <Typography
                                variant="subtitle1"
                                className="ellipsis-text"
                                color={
                                  onHover
                                    ? TP_COLORS.white
                                    : getSubjectColorNew(subjectItem.name)
                                }
                              >
                                {subjectItem.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                fontSize="12px"
                                marginBottom="2px"
                                color={onHover ? TP_COLORS.white : '#475569'}
                              >
                                {isSubjectCodeHide
                                  ? subjectItem.grade
                                  : subjectItem.subjectCode}
                              </Typography>
                              <Typography
                                variant="body2"
                                fontSize="12px"
                                color={onHover ? TP_COLORS.white : '#475569'}
                              >
                                {subjectItem.subjectLevel}
                              </Typography>
                            </Box>
                            <img
                              src={`${
                                process.env.PUBLIC_URL
                              }/images/landingPage/icon-${getSubjectIconName(
                                subjectItem.name
                              )}${onHover ? '-white' : ''}.avif`}
                              alt="subject-img"
                              style={{ height: '75px' }}
                            />
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </Collapse>
      ) : null}

      {RENDER_CURRICULUMS(selectedCurriculumIndex || 0, curriculumsList.length)}

      {isMobile && curriculumsForHomePage.length > curriculumsList.length && (
        <Stack alignItems="center">
          <Button
            color="secondary"
            onClick={() =>
              setCurriculumsList(
                curriculumsForHomePage.slice(0, curriculumsForHomePage.length)
              )
            }
            sx={{
              marginTop: '20px',
            }}
          >
            View more
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default Curriculums;
